import { useState, useEffect, useCallback } from "react";
import { useGetIdentity } from "ra-core";
import { RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getSupabaseClient } from "../providers/supabase/supabase";

type SyncLog = {
  id: number;
  synced_at: string;
  resource: string;
  records_upserted: number;
  records_archived: number;
  records_failed: number;
  triggered_by: string;
  delta_since: string | null;
};

const RESOURCES = [
  "customers",
  "contacts",
  "salespeople",
  "interactions",
  "segments",
] as const;

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

async function readBcSyncErrorMessage(
  error: unknown,
  data?: unknown,
): Promise<string> {
  const formatUnknown = (value: unknown): string => {
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      if (typeof record.message === "string" && record.message) {
        return record.message;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  if (data && typeof data === "object") {
    const payload = data as { errors?: unknown[]; success?: boolean };
    if (payload.errors?.length) {
      return payload.errors.map(formatUnknown).join(" · ");
    }
    if (payload.success === false) return "Sync failed";
  }

  const fallback = formatUnknown(error);
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === "function") {
    try {
      const payload = await context.json();
      if (payload && typeof payload === "object") {
        const errors = (payload as { errors?: unknown[] }).errors;
        if (errors?.length) return errors.map(formatUnknown).join(" · ");
        if ("message" in payload) return formatUnknown(payload.message);
        if ("error" in payload) return formatUnknown(payload.error);
      }
    } catch {
      // ignore parse errors
    }
  }
  return fallback || "Sync failed";
}

export const BCSyncPanel = () => {
  const { data: identity } = useGetIdentity();
  if (!identity?.administrator) return null;
  return <BCSyncPanelContent />;
};

const BCSyncPanelContent = () => {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    const { data } = await getSupabaseClient()
      .from("bc_sync_log")
      .select("*")
      .order("synced_at", { ascending: false })
      .limit(30);
    if (data) setLogs(data as SyncLog[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const lastFor = (resource: string) =>
    logs.find((l) => l.resource === resource);

  const handleSync = async (
    resource: "all" | (typeof RESOURCES)[number],
    options?: { full?: boolean },
  ) => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "bc-sync",
        { body: { resource, full: options?.full ?? false } },
      );
      if (error) {
        throw new Error(await readBcSyncErrorMessage(error, data));
      }
      if (data?.success === false) {
        throw new Error(await readBcSyncErrorMessage(null, data));
      }
      const results = data?.results ?? {};
      const summary = Object.entries(results)
        .map(
          ([r, s]: [string, any]) =>
            `${r}: ${s.upserted} upserted${s.failed ? `, ${s.failed} failed` : ""}`,
        )
        .join(" · ");
      setSyncResult(summary || "Sync complete");
      await fetchLogs();
    } catch (e: unknown) {
      setSyncError(
        e instanceof Error ? e.message : await readBcSyncErrorMessage(e),
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card id="bc-sync">
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-muted-foreground">
            Business Central Sync
          </h2>
          <Button
            type="button"
            onClick={() => handleSync("all")}
            disabled={syncing}
            size="sm"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        {syncResult && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {syncResult}
          </div>
        )}
        {syncError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {syncError}
          </div>
        )}

        <Separator />

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading sync status…</p>
        ) : (
          <div className="space-y-4">
            {RESOURCES.map((resource) => {
              const last = lastFor(resource);
              return (
                <div
                  key={resource}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium capitalize">{resource}</p>
                    {last ? (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span>{formatDate(last.synced_at)}</span>
                        <span>·</span>
                        <span>{last.records_upserted} upserted</span>
                        {last.records_failed > 0 && (
                          <>
                            <span>·</span>
                            <span className="text-destructive">
                              {last.records_failed} failed
                            </span>
                          </>
                        )}
                        <span>·</span>
                        <span>{last.triggered_by}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Never synced
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {resource === "contacts" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(resource, { full: true })}
                        disabled={syncing}
                        title="Re-fetch all BC contacts and re-link companies"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                        />
                        Full relink
                      </Button>
                    )}
                    {resource === "segments" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSync(resource, { full: true })}
                        disabled={syncing}
                        title="Re-fetch all BC segments and rebuild contact sector memberships"
                      >
                        <RefreshCw
                          className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                        />
                        Full relink
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(resource)}
                      disabled={syncing}
                    >
                      <RefreshCw
                        className={`h-3 w-3 mr-1.5 ${syncing ? "animate-spin" : ""}`}
                      />
                      Sync
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
